import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { endpoints } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import useFirebaseApp from 'src/hooks/use-firebase-app';
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updatePassword
} from 'firebase/auth';
import OneSignal from 'react-onesignal';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { firebaseAuth } = useFirebaseApp();

  // LOGOUT
  const logout = useCallback(async () => {
    signOut(firebaseAuth);
    setSession(null);

    OneSignal.logout();

    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const initialize = useCallback(async () => {
    try {
      const accessToken = await firebaseAuth.currentUser?.getIdToken();

      console.log(accessToken);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const user = firebaseAuth.currentUser;

        dispatch({
          type: 'INITIAL',
          payload: {
            user,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      logout();
    }
  }, [logout]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      initialize();
    });
    return () => unsubscribe();
  }, [initialize]);

  // ACCESS TOKEN
  const requestAccessToken = useCallback(async () => {
    console.log("Access token requested");
    const response = await firebaseAuth.currentUser.getIdToken(true);
    console.log("Access token received: " + response);

    return response;
  }, []);

  // FIREBASE USERID
  const requestFirebaseUserID = useCallback(() => {
    return firebaseAuth.currentUser.uid;
  }, []);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const firebaseResponse = await signInWithEmailAndPassword(firebaseAuth, email, password);
    console.log({ firebaseResponse });
  }, []);

  const forgotPassword = useCallback(async (email) => {
    console.log("Forgot password requested for " + email);
    const response = await sendPasswordResetEmail(firebaseAuth, email);
    return response;
  }, []);

  const requestUpdatePassword = useCallback(async (newPassword) => {
    console.log("Update password requested");
    const response = await updatePassword(firebaseAuth.currentUser, newPassword);
    return response;
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const data = {
      email,
      password,
      firstName,
      lastName,
    };

    const response = await axios.post(endpoints.auth.register, data);

    const { accessToken, user } = response.data;

    sessionStorage.setItem(STORAGE_KEY, accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      requestAccessToken,
      login,
      register,
      forgotPassword,
      requestUpdatePassword,
      logout,
      requestFirebaseUserID
    }),
    [requestAccessToken, login, logout, register, forgotPassword, requestUpdatePassword, requestFirebaseUserID, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
